import axios from 'axios';

/**
 * Single axios instance for the B2B portal.
 *
 * Base URL = VITE_API_URL (e.g. https://api.amabec.ca in prod).
 * Every request is auto-prefixed with `/api` so callers just write
 * `api.get('/b2b/products')` and the actual path is
 * `{baseURL}/api/b2b/products`. Matches the ERP convention.
 *
 * withCredentials = true: the b2b_access_token httpOnly cookie set by
 * the backend flows on every request without manual handling.
 */

// VITE_API_URL is the explicit override (local dev: http://localhost:3000).
// Fallback: localhost → the local backend; anything else → the prod API.
// The previous fallback (window.location.origin) sent API calls to the
// static Pages host whenever VITE_API_URL was missing from the Cloudflare
// build env — every POST died as a bare 405.
const isLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
const baseURL = import.meta.env.VITE_API_URL || (isLocalhost ? 'http://localhost:3000' : 'https://api.amabec.ca');

const api = axios.create({
    baseURL,
    withCredentials: true,
    headers: { 'Content-Type': 'application/json' },
    timeout: 30_000,
});

// CSRF double-submit: the backend protects authenticated mutations
// (POST /b2b/orders, /b2b/auth/logout) with a cookie+header check.
// Pre-auth endpoints (login, activate, reset-*) are exempt server-side.
// We fetch the token lazily before the first mutation, cache it in
// memory, and retry once with a fresh token on a CSRF 403.
let csrfToken = null;
let csrfPromise = null;

function fetchCsrfToken() {
    if (!csrfPromise) {
        csrfPromise = api.get('/csrf-token')
            .then(({ data }) => {
                csrfToken = data.csrfToken;
                return csrfToken;
            })
            .catch(() => null) // proceed without token: exempted routes still work
            .finally(() => { csrfPromise = null; });
    }
    return csrfPromise;
}

const MUTATING_METHODS = new Set(['post', 'put', 'delete', 'patch']);

api.interceptors.request.use(async (config) => {
    if (config.url && !config.url.startsWith('http') && !config.url.startsWith('/api')) {
        config.url = `/api${config.url.startsWith('/') ? '' : '/'}${config.url}`;
    }
    if (MUTATING_METHODS.has((config.method || 'get').toLowerCase())) {
        const token = csrfToken || await fetchCsrfToken();
        if (token) config.headers['X-CSRF-Token'] = token;
    }
    return config;
});

// Session-expired handler: if the backend returns 401 on a non-auth call,
// kick the user back to /login. We skip this on /auth/login itself so
// the login screen can show its own error message.
api.interceptors.response.use(
    (resp) => resp,
    async (err) => {
        const status = err?.response?.status;
        const url = err?.config?.url || '';
        const code = err?.response?.data?.error?.code || '';

        // CSRF token rejected (expired or mismatched cookie): refresh once
        // and retry the original request with the fresh token.
        if (status === 403 && code.startsWith('CSRF') && !err.config._csrfRetried) {
            err.config._csrfRetried = true;
            csrfToken = null;
            await fetchCsrfToken();
            if (csrfToken) {
                err.config.headers['X-CSRF-Token'] = csrfToken;
                return api(err.config);
            }
        }

        // /auth/me is the session probe fired on every app boot — a 401
        // there is the NORMAL anonymous state, not an expired session.
        // Redirecting on it bounced every fresh visitor (e.g. someone
        // opening an /activate?token= link) straight to /login.
        const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/reset-')
            || url.includes('/auth/activate') || url.includes('/auth/me');
        if (status === 401 && !isAuthEndpoint) {
            if (window.location.pathname !== '/login') {
                const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
                window.location.href = `/login?returnTo=${returnTo}`;
            }
        }
        return Promise.reject(err);
    }
);

export default api;
